import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "../badge";
import { Button } from "@ndd/ui";
import { ChevronRight, RefreshCw } from "lucide-react";
import { authenticatedApi } from "../../lib/api";
import {
  SECTION_DISPLAY_NAMES,
  getNextIncompleteSection,
  type AnalysisResponse,
  type AnalysisStatus,
} from "../../../../shared/analysis-types";
import { formatLocalDate } from "../../../../shared/local-date";

export interface SessionRowData {
  id: string;
  shortId: string;
  clientName: string | null;
  sessionDate: string | null;
  sessionType: "initial" | "follow_up" | null;
  summary: string | null;
  promptVersion: string;
  status: AnalysisStatus;
  errorMessage: string | null;
  patientId: string | null;
  /**
   * NO-775: a pasted Session waiting for Healthie's Transcript to queue its
   * Analysis. Absent on responses that do not project it.
   */
  awaitingHealthieTranscript?: boolean;
}

interface SessionRowProps {
  rdId: string;
  rdSlug: string;
  session: SessionRowData;
  showPatientLinkStatus: boolean;
  isSelected: boolean;
  onSelect: (sessionId: string) => void;
}

/**
 * What one row shows. NO-775: `waiting` is a pasted Session whose Analysis
 * starts only once Healthie delivers its Transcript; it is not analyzing, so
 * it never polls the Analysis endpoint.
 */
export type SessionRowState = "waiting" | "analyzing" | "error" | "ready";

export function sessionRowState(
  session: Pick<SessionRowData, "status" | "awaitingHealthieTranscript">,
  retriedToPending: boolean
): SessionRowState {
  if (retriedToPending) return "analyzing";

  if (session.awaitingHealthieTranscript === true) return "waiting";

  if (session.status === "pending" || session.status === "analyzing")
    return "analyzing";

  return session.status === "error" ? "error" : "ready";
}

/** How often the Session list re-reads while a pasted Session waits for Healthie. */
export const WAITING_SESSIONS_REFRESH_MS = 60_000;

/**
 * NO-775: a waiting row never polls its Analysis, so the list itself re-reads
 * `GET /api/rds/:rdId/sessions` slowly while any listed Session waits for
 * Healthie's Transcript, and not at all otherwise. React Query's function
 * form of `refetchInterval` calls this with the cached list.
 */
export function sessionListRefetchInterval(
  sessions:
    | ReadonlyArray<Pick<SessionRowData, "awaitingHealthieTranscript">>
    | undefined
): number | false {
  const anyWaiting = sessions?.some(
    (session) => session.awaitingHealthieTranscript === true
  );

  return anyWaiting ? WAITING_SESSIONS_REFRESH_MS : false;
}

/** Only an analyzing row polls `GET /api/sessions/:id/analysis`. */
export function pollsAnalysis(state: SessionRowState): boolean {
  return state === "analyzing";
}

function useAnalysisProgress(
  sessionId: string,
  isActive: boolean,
  onTerminal: () => void
) {
  const [progressSection, setProgressSection] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isActive) return;

    async function poll() {
      try {
        const res = await fetch(`/api/sessions/${sessionId}/analysis`, {
          credentials: "same-origin",
        });

        if (!res.ok) return;

        const { data } = (await res.json()) as { data: AnalysisResponse };

        if (data.status === "complete" || data.status === "error") {
          if (intervalRef.current) clearInterval(intervalRef.current);

          onTerminal();

          return;
        }

        const next = getNextIncompleteSection(data.sections ?? {});
        setProgressSection(next ? SECTION_DISPLAY_NAMES[next] : null);
      } catch {
        // silently ignore polling errors
      }
    }

    void poll();
    intervalRef.current = setInterval(() => void poll(), 1500);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [sessionId, isActive, onTerminal]);

  return isActive ? progressSection : null;
}

function useSessionRetry(rdId: string, sessionId: string, onRetried: () => void) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [retrying, setRetrying] = useState(false);

  const handleRetry = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setRetrying(true);

      try {
        await authenticatedApi(getToken, `/api/sessions/${sessionId}/retry`, {
          method: "POST",
        });
        onRetried();
        void queryClient.invalidateQueries({ queryKey: ["rd-sessions", rdId] });
      } catch {
        // parity with legacy: a failed retry keeps the error row as-is
      } finally {
        setRetrying(false);
      }
    },
    [getToken, queryClient, rdId, sessionId, onRetried]
  );

  return { retrying, handleRetry };
}

/** The row's state, its Analysis progress while analyzing, and its Retry. */
function useRowAnalysis(rdId: string, session: SessionRowData) {
  const queryClient = useQueryClient();
  const [retriedToPending, setRetriedToPending] = useState(false);
  const state = sessionRowState(session, retriedToPending);
  const handleTerminal = useCallback(() => {
    setRetriedToPending(false);
    void queryClient.invalidateQueries({ queryKey: ["rd-sessions", rdId] });
    void queryClient.invalidateQueries({ queryKey: ["rd", rdId] });
  }, [queryClient, rdId]);
  const progressSection = useAnalysisProgress(
    session.id,
    pollsAnalysis(state),
    handleTerminal
  );
  const markRetried = useCallback(() => setRetriedToPending(true), []);
  const { retrying, handleRetry } = useSessionRetry(rdId, session.id, markRetried);

  return { state, progressSection, retrying, handleRetry };
}

export function SessionRow({
  rdId,
  rdSlug,
  session,
  showPatientLinkStatus,
  isSelected,
  onSelect,
}: SessionRowProps) {
  const { state, progressSection, retrying, handleRetry } = useRowAnalysis(
    rdId,
    session
  );
  const showUnlinked = showPatientLinkStatus && state === "ready" && !session.patientId;

  const handleClick = useRowSelect(onSelect, session.id);

  return (
    <Link
      to="/rds/$rdId/sessions/$sessionId"
      params={{ rdId: rdSlug, sessionId: session.shortId }}
      onClick={handleClick}
      className={rowClassName(isSelected)}
    >
      {/* Date */}
      <span className="w-[80px] shrink-0 text-sm text-muted-foreground">
        {formatLocalDate(session.sessionDate) || "—"}
      </span>

      {/* Type / status badge */}
      <span className="shrink-0">
        <SessionRowBadge state={state} sessionType={session.sessionType} />
      </span>

      {showUnlinked && (
        <span className="shrink-0">
          <Badge variant="category">Unlinked</Badge>
        </span>
      )}

      {/* Client name + summary / progress / error */}
      <span className="flex-1 min-w-0 flex items-center gap-1">
        <SessionRowSummary
          state={state}
          session={session}
          progressSection={progressSection}
        />
      </span>

      {/* Right action: retry or chevron */}
      {state === "error" ? (
        <RetryButton retrying={retrying} onRetry={(e) => void handleRetry(e)} />
      ) : (
        <ChevronRight className="shrink-0 h-4 w-4 text-sand-300" />
      )}
    </Link>
  );
}

// Modifier/middle click: let the Link navigate natively (new tab etc.)
function isNativeNavigation(e: React.MouseEvent): boolean {
  const withModifier = e.metaKey || e.ctrlKey || e.shiftKey;

  return withModifier || e.button === 1;
}

function useRowSelect(onSelect: (sessionId: string) => void, sessionId: string) {
  return useCallback(
    (e: React.MouseEvent) => {
      if (isNativeNavigation(e)) return;

      e.preventDefault();
      onSelect(sessionId);
    },
    [onSelect, sessionId]
  );
}

function rowClassName(isSelected: boolean): string {
  const selection = isSelected
    ? "border-l-4 border-l-primary bg-info-soft/30"
    : "border-l-4 border-l-transparent hover:bg-muted";

  return [
    "flex items-center gap-4 w-full text-left py-3 px-5 border-b border-border transition-colors",
    selection,
  ].join(" ");
}

export function SessionRowBadge({
  state,
  sessionType,
}: {
  state: SessionRowState;
  sessionType: SessionRowData["sessionType"];
}) {
  if (state === "waiting") {
    return <Badge variant="category">Waiting for Healthie transcript</Badge>;
  }

  if (state === "analyzing") {
    return (
      <Badge variant="analyzing" className="gap-2">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-500 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-500" />
        </span>
        Analyzing
      </Badge>
    );
  }

  if (state === "error") return <Badge variant="error-status">Error</Badge>;

  return <SessionTypeBadge sessionType={sessionType} />;
}

function SessionTypeBadge({
  sessionType,
}: {
  sessionType: SessionRowData["sessionType"];
}) {
  if (sessionType === "initial") return <Badge variant="category">Initial</Badge>;

  if (sessionType === "follow_up") return <Badge variant="category">Follow-up</Badge>;

  return null;
}

export function SessionRowSummary({
  state,
  session,
  progressSection,
}: {
  state: SessionRowState;
  session: SessionRowData;
  progressSection: string | null;
}) {
  if (state === "analyzing") {
    return (
      <>
        <span className="text-sm text-foreground truncate">
          {session.clientName ?? "Processing"}
        </span>
        {progressSection && (
          <span className="text-xs text-subtle italic truncate">
            &middot; Working on {progressSection}
          </span>
        )}
      </>
    );
  }

  if (state === "error") {
    return (
      <>
        <span className="text-sm text-foreground truncate">
          {session.clientName || "Untitled Session"}
        </span>
        <span className="text-xs text-destructive truncate">
          &middot; {session.errorMessage ?? "Analysis failed"}
        </span>
      </>
    );
  }

  return (
    <span className="text-sm text-foreground truncate">
      {session.clientName || "Untitled Session"}
      {session.summary && (
        <span className="text-muted-foreground"> — {session.summary}</span>
      )}
    </span>
  );
}

function RetryButton({
  retrying,
  onRetry,
}: {
  retrying: boolean;
  onRetry: (e: React.MouseEvent) => void;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onRetry}
      disabled={retrying}
      className="shrink-0 text-destructive border-destructive hover:bg-danger-soft hover:text-destructive"
    >
      <RefreshCw className={retrying ? "animate-spin" : ""} />
      Retry
    </Button>
  );
}
